"use client";

import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface IconAvatarProps {
  /** Logo URL. Null/undefined shows the icon fallback immediately. */
  src?: string | null;
  /** Outer diameter in px. */
  size?: number;
  /**
   * Fallback content — pass an `<Icon />`. IconAvatar calculates the icon size
   * from `size` and injects it, so callers don't set a size on the icon.
   */
  icon?: ReactNode;
  /** Applied to the Avatar root — set rounding and background here. */
  className?: string;
  alt?: string;
}

/**
 * Avatar for a logo with a graceful icon fallback. Built on the Radix Avatar,
 * so the icon shows when the logo URL is missing OR fails to load — never a
 * broken image. The image is always contained and centred so logos aren't
 * cropped; the fallback icon is sized from `size`.
 */
export function IconAvatar({
  src,
  size = 32,
  icon,
  className,
  alt = "",
}: IconAvatarProps) {
  const iconSize = Math.max(8, Math.round(size * 0.5));
  const sizedIcon = isValidElement(icon)
    ? cloneElement(icon as ReactElement<{ size?: number }>, { size: iconSize })
    : icon;

  return (
    <Avatar
      className={cn("rounded-md bg-contrast/10", className)}
      style={{ width: size, height: size }}
    >
      {src && (
        <AvatarImage
          src={src}
          alt={alt}
          className="rounded-[inherit] object-contain object-center"
        />
      )}
      <AvatarFallback className="rounded-[inherit] bg-[inherit]">
        {sizedIcon}
      </AvatarFallback>
    </Avatar>
  );
}
